import React from 'react';
import {
    Box,
    Spinner,
    VStack,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { useAuth } from '../../auth/AuthContext';
import { Navigate } from 'react-router-dom';
import useAdminPermission from '../hooks/useAdminPermission';
import AccessDeniedPage from './AccessDeniedPage';

interface AdminGuardProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

/**
 * Komponen guard untuk memproteksi halaman admin
 * Melakukan pengecekan authentication dan admin permission
 */
const AdminGuard: React.FC<AdminGuardProps> = ({ children, fallback }) => {
    const { user, loading: authLoading } = useAuth();
    const {
        loading: permissionLoading,
        error: permissionError,
        hasAccess,
        permission,
        checkPermission
    } = useAdminPermission();

    const bgColor = useColorModeValue('gray.50', 'gray.900');

    // Show loading while checking authentication
    if (authLoading) {
        return (
            <Box
                minH="100vh"
                bg={bgColor}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" />
                    <Text>Checking authentication...</Text>
                </VStack>
            </Box>
        );
    }

    // Redirect to login if not authenticated
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Show loading while checking admin permission
    if (permissionLoading) {
        return (
            <Box
                minH="100vh"
                bg={bgColor}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" />
                    <Text>Checking admin permission...</Text>
                </VStack>
            </Box>
        );
    }

    // Show error state if permission check failed
    if (permissionError) {
        return (
            <AccessDeniedPage
                userEmail={user.email}
                message={`Error checking permission: ${permissionError}`}
                onRetry={checkPermission}
            />
        );
    }

    // Show access denied if user doesn't have admin access
    if (permission && !hasAccess) {
        return (
            <AccessDeniedPage
                userEmail={permission.user_email}
                message={permission.message}
                onRetry={checkPermission}
            />
        );
    }

    // Show custom fallback if provided and no access
    if (!hasAccess && fallback) {
        return <>{fallback}</>;
    }

    // Show access denied as default if no access
    if (!hasAccess) {
        return (
            <AccessDeniedPage
                userEmail={user.email}
                onRetry={checkPermission}
            />
        );
    }

    // Render children if user has admin access
    return <>{children}</>;
};

export default AdminGuard;
